import { LicenseInfo } from '@mui/x-license';
import { History } from 'history';
import React, { useState, Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

import { PermissionsProvider } from 'contexts/Permissions/Permissions';
import { Center, ChakraWrapper, Spinner, theme } from 'quotient';
import { ThemeProvider, THEME_ID, QToastProvider, QGlobalStyles } from 'quotient/vNext';
import { Events } from 'utils/events';

import { queryClientConfig } from './configs/react-query';
import i18n from './i18n';
import { TQRouter } from './TQRouter';

type Props = {
  history: History; // History object from shell app - now required
};

const App: React.VFC<Props> = ({ history }) => {
  LicenseInfo.setLicenseKey(process.env.MUI_X_LICENSE_KEY || '');
  i18n.changeLanguage('en-US'); // for now until we use user locale
  const [queryClient] = useState(() => new QueryClient(queryClientConfig));

  useEffect(() => {
    const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

    Events.track(Events.EVENT.tq.landing_page.displayed, {
      viewport_height: viewportHeight,
      viewport_width: viewportWidth,
    });
  }, []);
  /* eslint-disable react/jsx-max-depth */
  return (
    <I18nextProvider defaultNS="translation" i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <PermissionsProvider>
          <ChakraWrapper customStyle={{ display: 'flex', flexDirection: 'column' }} fullHeight isEnabled>
            <ThemeProvider defaultMode="light" theme={{ [THEME_ID]: theme }}>
              <QGlobalStyles
                styles={{
                  '.notistack-SnackbarContainer': {
                    zIndex: 9999, // Ensure toasts appear above modals (modals use z-index 1200-1400)
                  },
                }}
              />
              <QToastProvider>
                <HelmetProvider>
                  {!process.env.HIDE_REACT_QUERY_DEVTOOLS && <ReactQueryDevtools position="bottom-left" />}
                  <Suspense
                    fallback={
                      <Center h="100vh">
                        <Spinner />
                      </Center>
                    }
                  >
                    <TQRouter history={history} />
                  </Suspense>
                </HelmetProvider>
              </QToastProvider>
            </ThemeProvider>
          </ChakraWrapper>
        </PermissionsProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
};
export default App;
